// When a page is slow ON THE OWNER'S OWN MACHINE, say where the time went — as a feedback row I can read.
//
// ⚠ WHY THIS EXISTS (2026-09-17). "It is taking forever to load pages" — 5 to 10 seconds to open the stash — and every
// measurement I could take from a test tab said 100 ms. A hidden tab reports no main-thread stalls, and I cannot open DevTools
// on his screen. So the page measures itself, with the browser's own instruments, and files ONE short report per page load
// when — and only when — something was actually slow. Nothing is sent for a page that behaved.
//
// What it watches: long tasks (main-thread stalls over 50 ms, with the script file the browser blames), slow interactions
// (a click that took over 1 s to paint, with the element pressed), the page's own load, and how much code and how many
// requests it took. All of it is timings and element names — never what was typed, never any content.
import { sendFeedback } from "./feedback";

type Long = { at: number; ms: number; by: string };
type Slow = { at: number; ms: number; on: string };

const REPORT_AFTER_MS = 25_000;
const LONG_TOTAL_MS = 1500;
const INTERACTION_MS = 1000;

const label = (el: unknown): string => {
  const e = el as { tagName?: string; textContent?: string | null; getAttribute?: (n: string) => string | null } | null;
  if (!e?.tagName) return "?";
  const words = (e.getAttribute?.("aria-label") || e.textContent || "").replace(/\s+/g, " ").trim().slice(0, 32);
  return `${e.tagName.toLowerCase()}${words ? ` "${words}"` : ""}`;
};

export function watchForSlowness(where: () => string): void {
  if (typeof PerformanceObserver === "undefined") return;
  const longs: Long[] = [];
  const slows: Slow[] = [];
  try {
    new PerformanceObserver((l) => {
      for (const e of l.getEntries()) {
        const a = (e as unknown as { attribution?: { containerSrc?: string; containerType?: string }[] }).attribution?.[0];
        longs.push({ at: Math.round(e.startTime), ms: Math.round(e.duration), by: a?.containerSrc?.split("/").pop()?.replace(/-[\w-]{8}\.js$/, "") || a?.containerType || "" });
      }
    }).observe({ type: "longtask", buffered: true });
  } catch { /* not supported: the report just lacks stalls */ }
  try {
    new PerformanceObserver((l) => {
      for (const e of l.getEntries()) {
        const ev = e as PerformanceEntry & { target?: unknown; interactionId?: number };
        if (e.duration >= INTERACTION_MS && ev.interactionId) slows.push({ at: Math.round(e.startTime), ms: Math.round(e.duration), on: label(ev.target) });
      }
    }).observe({ type: "event", buffered: true, durationThreshold: INTERACTION_MS } as PerformanceObserverInit);
  } catch { /* fine */ }

  setTimeout(() => {
    const longTotal = longs.reduce((n, l) => n + l.ms, 0);
    const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
    const loadMs = nav ? Math.round(nav.loadEventEnd || nav.domContentLoadedEventEnd) : 0;
    if (longTotal < LONG_TOTAL_MS && !slows.length && loadMs < 4000) return;
    const res = performance.getEntriesByType("resource") as PerformanceResourceTiming[];
    const js = res.filter((r) => /\.js(\?|$)/.test(r.name));
    const mem = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
    const worst = [...longs].sort((a, b) => b.ms - a.ms).slice(0, 6).map((l) => `${l.ms}ms@${(l.at / 1000).toFixed(1)}s${l.by ? ` in ${l.by}` : ""}`).join(", ");
    const clicks = slows.slice(0, 6).map((s) => `${s.ms}ms on ${s.on}@${(s.at / 1000).toFixed(1)}s`).join("; ");
    const body = [
      `[auto] slow page — ${where()}`,
      `load ${loadMs}ms · main-thread stalls ${longs.length} totalling ${longTotal}ms${worst ? ` (worst: ${worst})` : ""}`,
      clicks ? `slow presses: ${clicks}` : "no slow presses",
      `${res.length} requests, ${js.length} scripts (${Math.round(js.reduce((n, r) => n + (r.decodedBodySize || 0), 0) / 1048576 * 10) / 10} MB, ${js.filter((r) => r.transferSize === 0).length} from cache)${mem ? `, heap ${Math.round(mem.usedJSHeapSize / 1048576)} MB` : ""}`,
      `${navigator.userAgent.replace(/^.*?(Chrome|Edg|Firefox|Safari)\/([\d.]+).*$/, "$1 $2")} · ${document.visibilityState} · ${screen.width}×${screen.height}${(navigator as unknown as { hardwareConcurrency?: number }).hardwareConcurrency ? ` · ${(navigator as unknown as { hardwareConcurrency?: number }).hardwareConcurrency} cores` : ""}`,
    ].join("\n");
    void sendFeedback({ campaignId: null, shareToken: null, page: `slow: ${where().slice(0, 300)}`, body, build: null }, () => { /* a report that fails to send is just a report */ });
  }, REPORT_AFTER_MS);
}
